/* App-version & update notifier.
 *
 * Polls /version.json (a tiny static file CRA can serve) every 5 min.
 * When the deployed version changes vs. the loaded one, shows a toast
 * with "Reload" CTA so users get fresh features without losing state.
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useI18n } from '../i18n';
import { checkForAppUpdate, applyAppUpdateNow, updaterBridge } from '../lib/liveUpdate';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

export default function UpdateNotifier() {
    const loadedVersion = useRef(null);
    const { locale } = useI18n();
    const da = locale === 'da';

    // Android app: pull the newest web release from growdaily.fun (no Play upload needed).
    useEffect(() => {
        if (!updaterBridge()) return undefined;
        const run = async (force) => {
            const status = await checkForAppUpdate({ force });
            if (status !== 'installed') return;
            toast.success(da ? '🌱 En ny version af GrowDaily er hentet' : '🌱 A new version of GrowDaily is ready', {
                duration: 20000,
                description: da ? 'Den starter næste gang du åbner appen.' : 'It starts next time you open the app.',
                action: { label: da ? 'Genstart nu' : 'Restart now', onClick: () => { applyAppUpdateNow(); } },
            });
        };
        run(true);
        let handle;
        const App = window.Capacitor?.Plugins?.App;
        if (App?.addListener) {
            const sub = App.addListener('appStateChange', ({ isActive }) => { if (isActive) run(false); });
            Promise.resolve(sub).then((h) => { handle = h; }).catch(() => {});
        }
        return () => { try { handle?.remove?.(); } catch { /* ignore */ } };
    }, [da]);

    useEffect(() => {
        let timer = null;
        const fetchVersion = async () => {
            try {
                const r = await fetch(`/version.json?ts=${Date.now()}`, { cache: 'no-store' });
                if (!r.ok) return;
                const data = await r.json();
                const v = data?.version;
                if (!v) return;
                if (!loadedVersion.current) {
                    loadedVersion.current = v;
                    return;
                }
                if (v !== loadedVersion.current) {
                    toast.success('🌱 A new version of GrowDaily is ready', {
                        duration: 30000,
                        action: {
                            label: 'Reload',
                            onClick: async () => {
                                // Clear SW + caches so we definitely load the new bundle
                                try {
                                    if ('serviceWorker' in navigator) {
                                        const regs = await navigator.serviceWorker.getRegistrations();
                                        await Promise.all(regs.map((r) => r.unregister()));
                                    }
                                    if (window.caches) {
                                        const keys = await caches.keys();
                                        await Promise.all(keys.map((k) => caches.delete(k)));
                                    }
                                } catch {}
                                window.location.reload();
                            },
                        },
                    });
                    loadedVersion.current = v;
                }
            } catch {}
        };
        fetchVersion();
        timer = setInterval(fetchVersion, POLL_INTERVAL_MS);
        const onFocus = () => fetchVersion();
        window.addEventListener('focus', onFocus);
        return () => {
            clearInterval(timer);
            window.removeEventListener('focus', onFocus);
        };
    }, []);

    return null;
}
